import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ClipboardCheck, CheckCircle, Loader2, PenLine, Camera, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import BrandLogo from '../components/BrandLogo';
import {
  VEHICLE_TYPES, MOTORIZED_TYPES, MOTORIZED_FIELDS, MOTORIZED_CHOICES,
  CONDITION_QUESTIONS, CONDITION_ITEMS, CERT_TEXT, INSPECTION_TEXT,
  isRelevant, ITEM_RATINGS,
} from '../data/tradeAppraisalConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DRAFT_KEY = 'rv_trade_appraisal_draft';

const emptyForm = () => ({
  customer_name: '', email: '', phone: '', salesperson: '',
  stock_interest: '', quote_number: '',
  vehicle_type: '', year: '', make: '', model: '', floorplan: '', slides: '',
  clear_title: '', salvage_title: '', financed: '', finance_with: '',
  current_payment: '', balance_owed: '',
  motorized: {},
  condition: {},
  exterior_rating: '', interior_rating: '',
  items: {},
  photos: [],
  certification: { signature: '', initials: '', ack_cert: false, ack_inspection: false },
});

const loadDraft = () => {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return { ...emptyForm(), ...JSON.parse(saved) };
  } catch { /* corrupt draft */ }
  return emptyForm();
};

const Section = ({ title, subtitle, children }) => (
  <section className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7">
    <h2 className="text-base md:text-lg font-bold text-slate-900">{title}</h2>
    {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
    <div className="mt-4">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {children}
  </div>
);

const Segmented = ({ value, options, onChange, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(value === opt ? '' : opt)}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
          value === opt
            ? 'bg-[#004E86] text-white border-[#004E86]'
            : 'bg-white text-slate-600 border-slate-300 hover:border-[#004E86]'
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export default function TradeAppraisalForm({ embedded = false }) {
  const [searchParams] = useSearchParams();
  const [dealerSlug, setDealerSlug] = useState(searchParams.get('dealer') || '');
  const sessionParam = searchParams.get('session') || '';
  const [form, setForm] = useState(loadDraft);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Prefill: buyers arriving from their dashboard carry ?session=<id>;
  // direct links may carry ?name/&email/&phone.
  useEffect(() => {
    const applyPrefill = (p) => setForm((f) => ({
      ...f,
      customer_name: f.customer_name || p.name || '',
      email: f.email || p.email || '',
      phone: f.phone || p.phone || '',
    }));
    if (sessionParam) {
      axios.get(`${API}/trade-appraisal/prefill/${sessionParam}`)
        .then((r) => {
          applyPrefill(r.data);
          if (r.data.dealer_slug) setDealerSlug((s) => s || r.data.dealer_slug);
        })
        .catch(() => { /* anonymous fill is fine */ });
    } else {
      applyPrefill({
        name: searchParams.get('name') || '',
        email: searchParams.get('email') || '',
        phone: searchParams.get('phone') || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - (form.photos?.length || 0));
    if (files.length === 0) return;
    setUploadingPhotos(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/trade-appraisal/upload-photo`, fd);
        setForm((f) => ({ ...f, photos: [...(f.photos || []), r.data] }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Couldn't upload ${file.name}`);
      }
    }
    setUploadingPhotos(false);
    e.target.value = '';
  };

  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch { /* full */ }
    }, 500);
    return () => clearTimeout(t);
  }, [form]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setMotorized = (key, value) => setForm((f) => ({ ...f, motorized: { ...f.motorized, [key]: value } }));
  const setCondition = (key, patch) =>
    setForm((f) => ({ ...f, condition: { ...f.condition, [key]: { ...(f.condition[key] || {}), ...patch } } }));
  const setItem = (key, patch) =>
    setForm((f) => ({ ...f, items: { ...f.items, [key]: { ...(f.items[key] || {}), ...patch } } }));
  const setCert = (key, value) =>
    setForm((f) => ({ ...f, certification: { ...f.certification, [key]: value } }));

  const isMotorized = useMemo(() => MOTORIZED_TYPES.includes(form.vehicle_type), [form.vehicle_type]);
  const relevantQuestions = useMemo(() => CONDITION_QUESTIONS.filter((q) => isRelevant(q, form.vehicle_type)), [form.vehicle_type]);
  const relevantItems = useMemo(() => CONDITION_ITEMS.filter((it) => isRelevant(it, form.vehicle_type)), [form.vehicle_type]);

  const questionsAnswered = relevantQuestions.filter((q) => form.condition[q.key]?.answer).length;
  const itemsRated = relevantItems.filter((it) => form.items[it.key]?.rating).length;
  const motorizedChoicesAnswered = MOTORIZED_CHOICES.filter((c) => form.motorized[c.key]).length;

  // Every category must be complete for the selected vehicle type before
  // the certificate can be submitted (irrelevant sections are hidden).
  const getMissing = () => {
    const missing = [];
    if (!form.customer_name.trim()) missing.push('your name');
    if (!form.phone.trim() && !form.email.trim()) missing.push('a phone number or email');
    if (!form.vehicle_type) missing.push('vehicle type');
    if (!form.year.trim() || !form.make.trim() || !form.model.trim()) missing.push('year / make / model');
    if (!form.clear_title) missing.push('clear title (yes/no)');
    if (!form.salvage_title) missing.push('salvage title (yes/no)');
    if (!form.financed) missing.push('financed (yes/no)');
    if (form.financed === 'Yes' && (!form.finance_with.trim() || !form.balance_owed.trim())) missing.push('lender and balance owed');
    if (isMotorized) {
      if (!(form.motorized.odometer || '').trim()) missing.push('odometer reading');
      const mc = MOTORIZED_CHOICES.filter((c) => !form.motorized[c.key]).length;
      if (mc > 0) missing.push(`${mc} motorized question${mc === 1 ? '' : 's'}`);
    }
    const cq = relevantQuestions.length - questionsAnswered;
    if (cq > 0) missing.push(`${cq} condition question${cq === 1 ? '' : 's'}`);
    if (!form.exterior_rating) missing.push('exterior rating');
    if (!form.interior_rating) missing.push('interior rating');
    const ci = relevantItems.length - itemsRated;
    if (ci > 0) missing.push(`${ci} systems & equipment rating${ci === 1 ? '' : 's'} (use N/A if not equipped)`);
    if (!form.certification.signature.trim()) missing.push('signature');
    if (!form.certification.ack_cert) missing.push('certification checkbox');
    return missing;
  };

  const handleSubmit = async () => {
    const missing = getMissing();
    if (missing.length > 0) {
      setShowErrors(true);
      toast.error(`Almost there — still needed: ${missing.slice(0, 4).join(', ')}${missing.length > 4 ? ` and ${missing.length - 4} more` : ''}.`);
      return;
    }
    setSubmitting(true);
    try {
      // Persist only the answers relevant to this vehicle type.
      const condition = Object.fromEntries(relevantQuestions.map((q) => [q.key, form.condition[q.key]]));
      const items = Object.fromEntries(relevantItems.map((it) => [it.key, form.items[it.key]]));
      await axios.post(`${API}/trade-appraisal/submit`, {
        dealer_slug: dealerSlug,
        form: {
          ...form, condition, items,
          motorized: isMotorized ? form.motorized : {},
          certification: { ...form.certification, signed_at: new Date().toISOString() },
        },
      });
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md text-center" data-testid="trade-appraisal-success">
          <CheckCircle className="mx-auto text-emerald-500 mb-4" size={48} />
          <h1 className="text-xl font-bold text-slate-900">Trade Appraisal Submitted!</h1>
          <p className="text-sm text-slate-600 mt-2">
            Thanks, {form.customer_name.split(' ')[0] || 'friend'} — your advisor has your trade details and will
            follow up with your appraisal. Remember: a quick 1-hour tech check happens before the walk-thru.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-slate-50 ${embedded ? '' : 'pt-4'}`} data-testid="trade-appraisal-form">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="bg-[#004E86] rounded-2xl p-6 sm:p-8 text-white">
          <BrandLogo size="sm" variant="wordmark" theme="dark" />
          <div className="flex items-center gap-3 mt-4">
            <ClipboardCheck size={28} className="text-[#F2A900] shrink-0" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold">Trade Appraisal Certificate</h1>
              <p className="text-blue-100 text-sm mt-1">
                Tell us about your current RV — the more accurate you are, the stronger and faster your trade offer.
                Your progress saves automatically.
              </p>
            </div>
          </div>
        </div>

        {/* 1. Contact */}
        <Section title="Your Info" subtitle="So your advisor can attach this to your deal.">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name *">
              <Input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} placeholder="Jane Camper" data-testid="trade-customer-name" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(555) 555-5555" data-testid="trade-phone" />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" data-testid="trade-email" />
            </Field>
            <Field label="Salesperson / Advisor (if known)">
              <Input value={form.salesperson} onChange={(e) => set('salesperson', e.target.value)} placeholder="Optional" />
            </Field>
            <Field label="Stock # of Interest">
              <Input value={form.stock_interest} onChange={(e) => set('stock_interest', e.target.value)} placeholder="Unit you want to buy (optional)" />
            </Field>
            <Field label="Quote #">
              <Input value={form.quote_number} onChange={(e) => set('quote_number', e.target.value)} placeholder="Optional" />
            </Field>
          </div>
        </Section>

        {/* 2. Your trade */}
        <Section title="Your Trade-In" subtitle="The RV you're trading.">
          <Field label={<>Vehicle Type *{showErrors && !form.vehicle_type && <span className="ml-2 text-[10px] font-bold text-rose-500 uppercase">Required</span>}</>}>
            <Segmented value={form.vehicle_type} options={VEHICLE_TYPES} onChange={(v) => set('vehicle_type', v)} testId="trade-vehicle-type" />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
            <Field label="Year *"><Input value={form.year} onChange={(e) => set('year', e.target.value)} placeholder="2019" className={showErrors && !form.year.trim() ? 'border-rose-400' : ''} data-testid="trade-year" /></Field>
            <Field label="Make *"><Input value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="Jayco" className={showErrors && !form.make.trim() ? 'border-rose-400' : ''} data-testid="trade-make" /></Field>
            <Field label="Model / Size *"><Input value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="Jay Flight 28BHS" className={showErrors && !form.model.trim() ? 'border-rose-400' : ''} data-testid="trade-model" /></Field>
            <Field label="Floorplan"><Input value={form.floorplan} onChange={(e) => set('floorplan', e.target.value)} placeholder="Bunkhouse" /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
            <Field label="# of Slide-Outs"><Input value={form.slides} onChange={(e) => set('slides', e.target.value)} placeholder="0" /></Field>
            <Field label={<>Clear Title? *{showErrors && !form.clear_title && <span className="ml-1 text-[10px] font-bold text-rose-500 uppercase">Required</span>}</>}><Segmented value={form.clear_title} options={['Yes', 'No']} onChange={(v) => set('clear_title', v)} /></Field>
            <Field label={<>Salvage Title? *{showErrors && !form.salvage_title && <span className="ml-1 text-[10px] font-bold text-rose-500 uppercase">Required</span>}</>}><Segmented value={form.salvage_title} options={['Yes', 'No']} onChange={(v) => set('salvage_title', v)} /></Field>
          </div>
          <div className="mt-4">
            <Field label={<>RV Currently Financed? *{showErrors && !form.financed && <span className="ml-1 text-[10px] font-bold text-rose-500 uppercase">Required</span>}</>}>
              <Segmented value={form.financed} options={['Yes', 'No']} onChange={(v) => set('financed', v)} testId="trade-financed" />
            </Field>
            {form.financed === 'Yes' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                <Field label="With whom? *"><Input value={form.finance_with} onChange={(e) => set('finance_with', e.target.value)} placeholder="Lender" className={showErrors && !form.finance_with.trim() ? 'border-rose-400' : ''} /></Field>
                <Field label="Current Payment $"><Input value={form.current_payment} onChange={(e) => set('current_payment', e.target.value)} placeholder="350" /></Field>
                <Field label="Balance Owed $ *"><Input value={form.balance_owed} onChange={(e) => set('balance_owed', e.target.value)} placeholder="18,500" className={showErrors && !form.balance_owed.trim() ? 'border-rose-400' : ''} /></Field>
              </div>
            )}
          </div>
        </Section>

        {/* 3. Motorized (conditional) */}
        {isMotorized && (
          <Section
            title={`Motorized Details (${motorizedChoicesAnswered}/${MOTORIZED_CHOICES.length} questions answered)`}
            subtitle="Since your trade is a motorhome, these are required."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {MOTORIZED_FIELDS.map((f) => (
                <Field key={f.key} label={f.key === 'odometer' ? 'Odometer Reading *' : f.label}>
                  <Input
                    value={form.motorized[f.key] || ''}
                    onChange={(e) => setMotorized(f.key, e.target.value)}
                    className={showErrors && f.key === 'odometer' && !(form.motorized.odometer || '').trim() ? 'border-rose-400' : ''}
                  />
                </Field>
              ))}
            </div>
            <div className="space-y-3 mt-4">
              {MOTORIZED_CHOICES.map((c) => (
                <Field key={c.key} label={<>
                  {c.label}
                  {showErrors && !form.motorized[c.key] && <span className="ml-2 text-[10px] font-bold text-rose-500 uppercase">Required</span>}
                </>}>
                  <Segmented value={form.motorized[c.key] || ''} options={c.options} onChange={(v) => setMotorized(c.key, v)} />
                </Field>
              ))}
            </div>
          </Section>
        )}

        {/* 4. Condition questions */}
        <Section
          title={`Condition Check (${questionsAnswered}/${relevantQuestions.length} answered)`}
          subtitle={`Every question below applies to your ${form.vehicle_type || 'RV'} and must be answered. Honest answers protect your trade value — surprises found in inspection lower offers.`}
        >
          <div className="divide-y divide-slate-100">
            {relevantQuestions.map((q) => {
              const ans = form.condition[q.key] || {};
              const missing = showErrors && !ans.answer;
              return (
                <div key={q.key} className={`py-3 ${missing ? 'border-l-2 border-rose-400 pl-2 -ml-2 bg-rose-50/50' : ''}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-slate-700 flex-1 min-w-[200px]">
                      {q.label}
                      {missing && <span className="ml-2 text-[10px] font-bold text-rose-500 uppercase">Required</span>}
                    </p>
                    <Segmented value={ans.answer || ''} options={['Yes', 'No']} onChange={(v) => setCondition(q.key, { answer: v })} testId={`trade-cond-${q.key}`} />
                  </div>
                  {q.describe && ans.answer === 'Yes' && (
                    <Input
                      className="mt-2"
                      value={ans.note || ''}
                      onChange={(e) => setCondition(q.key, { note: e.target.value })}
                      placeholder={q.describe}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <Field label={<>Overall Exterior Rating (1–10) *{form.exterior_rating ? ` — ${form.exterior_rating}` : ''}{showErrors && !form.exterior_rating && <span className="ml-2 text-[10px] font-bold text-rose-500 uppercase">Required</span>}</>}>
              <Segmented value={form.exterior_rating} options={['1','2','3','4','5','6','7','8','9','10']} onChange={(v) => set('exterior_rating', v)} testId="trade-ext-rating" />
            </Field>
            <Field label={<>Overall Interior Rating (1–10) *{form.interior_rating ? ` — ${form.interior_rating}` : ''}{showErrors && !form.interior_rating && <span className="ml-2 text-[10px] font-bold text-rose-500 uppercase">Required</span>}</>}>
              <Segmented value={form.interior_rating} options={['1','2','3','4','5','6','7','8','9','10']} onChange={(v) => set('interior_rating', v)} testId="trade-int-rating" />
            </Field>
          </div>
        </Section>

        {/* 5. Items table */}
        <Section
          title={`Systems & Equipment (${itemsRated}/${relevantItems.length} rated)`}
          subtitle="Rate every item — choose N/A if your RV doesn't have it. Add a note if there's anything worth mentioning."
        >
          <div className="divide-y divide-slate-100">
            {relevantItems.map((item) => {
              const val = form.items[item.key] || {};
              const missing = showErrors && !val.rating;
              return (
                <div key={item.key} className={`py-3 ${missing ? 'border-l-2 border-rose-400 pl-2 -ml-2 bg-rose-50/50' : ''}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-sm font-medium text-slate-800">
                        {item.label}
                        {missing && <span className="ml-2 text-[10px] font-bold text-rose-500 uppercase">Required</span>}
                      </p>
                      <p className="text-[11px] text-slate-400">{item.hint}</p>
                    </div>
                    <Segmented
                      value={val.rating || ''}
                      options={ITEM_RATINGS}
                      onChange={(v) => setItem(item.key, { rating: v })}
                      testId={`trade-item-${item.key}`}
                    />
                  </div>
                  {(val.rating === 'Poor' || val.note) && (
                    <Input
                      className="mt-2"
                      value={val.note || ''}
                      onChange={(e) => setItem(item.key, { note: e.target.value })}
                      placeholder="Describe / when?"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* 5.5 Photos (optional) */}
        <Section title="Photos of Your Trade (optional)" subtitle="A few honest photos help your advisor start the appraisal before you even arrive — exterior sides, roof if safe, and the interior.">
          <div className="flex flex-wrap gap-3">
            {(form.photos || []).map((p) => (
              <div key={p.id} className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200" data-testid={`trade-photo-${p.id}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="Trade" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x.id !== p.id) }))}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"
                  aria-label="Remove photo"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {(form.photos || []).length < 6 && (
              <label className={`w-24 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1 cursor-pointer text-slate-400 hover:border-[#004E86] hover:text-[#004E86] transition-colors ${uploadingPhotos ? 'opacity-50 pointer-events-none' : ''}`}>
                {uploadingPhotos ? <Loader2 className="animate-spin" size={18} /> : <Camera size={18} />}
                <span className="text-[10px] font-semibold">{uploadingPhotos ? 'Uploading…' : 'Add photo'}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoUpload} data-testid="trade-photo-input" />
              </label>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Up to 6 photos, 8 MB each.</p>
        </Section>

        {/* 6. Certification */}
        <Section title="Certification & Signature">
          <p className="text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3">{CERT_TEXT}</p>
          <label className="flex items-start gap-2 mt-4 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={form.certification.ack_cert} onChange={(e) => setCert('ack_cert', e.target.checked)} data-testid="trade-ack-cert" />
            <span className="text-sm text-slate-700">I agree — the information above is true and accurate to the best of my knowledge. *</span>
          </label>
          <label className="flex items-start gap-2 mt-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={form.certification.ack_inspection} onChange={(e) => setCert('ack_inspection', e.target.checked)} data-testid="trade-ack-inspection" />
            <span className="text-sm text-slate-700">{INSPECTION_TEXT}</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <div className="sm:col-span-2">
              <Field label="Signature — type your full name *">
                <div className="relative">
                  <PenLine size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-9 italic" value={form.certification.signature} onChange={(e) => setCert('signature', e.target.value)} placeholder="Jane Camper" data-testid="trade-signature" />
                </div>
              </Field>
            </div>
            <Field label="Initials">
              <Input value={form.certification.initials} onChange={(e) => setCert('initials', e.target.value)} placeholder="JC" data-testid="trade-initials" />
            </Field>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full mt-6 h-12 bg-[#F2A900] hover:bg-[#d99800] text-black font-bold text-base rounded-xl"
            data-testid="trade-submit-btn"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : 'Submit Trade Appraisal'}
          </Button>
          <p className="text-[11px] text-slate-400 text-center mt-2">
            Date is recorded automatically on submission.
          </p>
        </Section>
      </div>
    </div>
  );
}
